import React, { useState, useRef } from 'react';
import { Mic, Upload, X, Play, Square, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { useToast } from '../hooks/use-toast';

const AudioRecorder = ({ wordId, wordText, onUploadSuccess, onClose }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const { toast } = useToast();

  const startRecording = async () => {
    try {
      // Check if mediaDevices is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('getUserMedia not supported');
      }

      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        } 
      });

      mediaRecorderRef.current = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm;codecs=opus') 
          ? 'audio/webm;codecs=opus' 
          : 'audio/webm'
      });
      
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = () => {
        const mimeType = mediaRecorderRef.current.mimeType || 'audio/webm';
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        setAudioBlob(audioBlob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.onerror = (event) => {
        console.error('MediaRecorder error:', event);
        toast({
          title: "Recording Error",
          description: "Recording failed. Please try again.",
          variant: "destructive"
        });
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      
      toast({
        title: "🎤 Recording started",
        description: "Speak clearly and press stop when finished",
      });
    } catch (error) {
      console.error('Error starting recording:', error);
      
      let errorMessage = "Could not access microphone.";
      
      if (error.name === 'NotAllowedError') {
        errorMessage = "Microphone access denied. Please allow microphone permissions and try again.";
      } else if (error.name === 'NotFoundError') {
        errorMessage = "No microphone found. Please connect a microphone.";
      } else if (error.name === 'NotSupportedError') {
        errorMessage = "Your browser doesn't support audio recording.";
      } else if (error.message === 'getUserMedia not supported') {
        errorMessage = "Your browser doesn't support microphone access.";
      } else if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
        errorMessage = "Microphone access requires HTTPS. Please use a secure connection.";
      }
      
      toast({
        title: "Recording Error",
        description: errorMessage,
        variant: "destructive"
      });
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      toast({
        title: "Recording stopped",
        description: "You can now preview or upload your recording",
      });
    }
  };

  const playRecording = () => {
    if (audioBlob) {
      const audio = new Audio(URL.createObjectURL(audioBlob));
      setIsPlaying(true);
      
      audio.onended = () => {
        setIsPlaying(false);
      };
      
      audio.play();
      
      toast({
        title: "🔊 Playing recording",
        description: "Listen to your pronunciation",
      });
    }
  };

  const checkMicrophoneAccess = async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('getUserMedia not supported');
      }

      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = devices.filter(device => device.kind === 'audioinput');
      
      if (audioInputs.length === 0) {
        throw new Error('No microphone found');
      }

      // Test microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(track => track.stop());

      toast({
        title: "✅ Microphone Access OK",
        description: `Found ${audioInputs.length} audio input device(s). Permissions granted.`,
      });
    } catch (error) {
      console.error('Microphone test failed:', error);
      
      let message = "Microphone test failed.";
      if (error.name === 'NotAllowedError') {
        message = "Permission denied. Please allow microphone access in browser settings.";
      } else if (error.name === 'NotFoundError') {
        message = "No microphone detected. Please connect an audio input device.";
      } else if (error.message === 'getUserMedia not supported') {
        message = "Browser doesn't support microphone access.";
      }

      toast({
        title: "❌ Microphone Test Failed",
        description: message,
        variant: "destructive"
      });
    }
  };

  const uploadRecording = async () => {
    if (!audioBlob) return;

    setIsUploading(true);
    
    try {
      const formData = new FormData();
      // Use .webm extension to match the actual recorded format
      // Backend will convert to MP3 for better cross-browser compatibility
      formData.append('audio_file', audioBlob, `${wordId}-pronunciation.webm`);

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/vocabulary/${wordId}/upload-audio`,
        {
          method: 'POST',
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const result = await response.json();
      
      toast({
        title: "✅ Audio uploaded successfully",
        description: "Your authentic pronunciation has been saved!",
      });

      onUploadSuccess(result.audioUrl);
      onClose();
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: "Upload Error",
        description: "Failed to upload audio. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // List of supported audio formats (more permissive validation)
    const supportedFormats = [
      'audio/mpeg', 'audio/mp3',           // MP3
      'audio/wav', 'audio/wave',           // WAV
      'audio/mp4', 'audio/m4a',            // M4A/AAC
      'audio/webm', 'audio/ogg',           // WebM/OGG
      'audio/flac',                        // FLAC
      'audio/aac',                         // AAC
    ];
    
    const fileExtension = file.name.toLowerCase().split('.').pop();
    const supportedExtensions = ['mp3', 'wav', 'm4a', 'aac', 'webm', 'ogg', 'flac'];
    
    // Validate by both MIME type and file extension
    const isValidMimeType = file.type.startsWith('audio/') || supportedFormats.includes(file.type);
    const isValidExtension = supportedExtensions.includes(fileExtension);
    
    if (!isValidMimeType && !isValidExtension) {
      toast({
        title: "Invalid file type",
        description: `File type: ${file.type || 'unknown'}, Extension: .${fileExtension}\nSupported: MP3, WAV, M4A, AAC, WebM, OGG, FLAC`,
        variant: "destructive"
      });
      event.target.value = ''; // Reset input
      return;
    }
    
    console.log(`File info: ${file.name}, Type: ${file.type}, Size: ${file.size} bytes`);
    
    // Special warning for M4A files
    if (fileExtension === 'm4a' || file.type === 'audio/mp4') {
      toast({
        title: "M4A Format Notice",
        description: "M4A files may have playback compatibility issues. The server will convert it to MP3 for better compatibility.",
        variant: "default"
      });
    }

    // Check file size (limit to 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Please select a file smaller than 10MB",
        variant: "destructive"
      });
      event.target.value = ''; // Reset input
      return;
    }

    setIsUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('audio_file', file);

      toast({
        title: "Uploading audio...",
        description: `Uploading ${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB)`,
      });

      console.log('Upload details:', {
        wordId,
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        url: `${process.env.REACT_APP_BACKEND_URL}/api/vocabulary/${wordId}/upload-audio`
      });

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/vocabulary/${wordId}/upload-audio`,
        {
          method: 'POST',
          body: formData,
        }
      );

      console.log('Upload response:', {
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries())
      });

      if (!response.ok) {
        const responseText = await response.text();
        console.error('Upload failed response:', responseText);
        
        let errorData;
        try {
          errorData = JSON.parse(responseText);
        } catch {
          errorData = { detail: responseText };
        }
        
        throw new Error(errorData?.detail || `Upload failed: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      console.log('Upload success:', result);
      
      toast({
        title: "✅ Audio uploaded successfully",
        description: "Your audio file has been saved!",
      });

      onUploadSuccess(result.audioUrl);
      onClose();
    } catch (error) {
      console.error('Upload error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      
      let userMessage = "Failed to upload audio file. ";
      
      if (error.message.includes('Failed to fetch')) {
        userMessage += "Network error - please check your connection.";
      } else if (error.message.includes('413')) {
        userMessage += "File too large for server.";
      } else if (error.message.includes('415')) {
        userMessage += "File type not supported by server.";
      } else if (error.message.includes('500')) {
        userMessage += "Server error - please try again.";
      } else {
        userMessage += error.message || "Please try again.";
      }
      
      toast({
        title: "Upload Error",
        description: userMessage,
        variant: "destructive"
      });
    } finally {
      setIsUploading(false);
      event.target.value = ''; // Reset input
    }
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">Record Pronunciation</CardTitle>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>
        <p className="text-gray-600">
          Record authentic pronunciation for: <strong>{wordText}</strong>
        </p>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Recording Controls */}
        <div className="flex justify-center space-x-4">
          {!isRecording ? (
            <Button onClick={startRecording} size="lg" className="bg-red-600 hover:bg-red-700">
              <Mic className="w-5 h-5 mr-2" />
              Start Recording
            </Button>
          ) : (
            <Button onClick={stopRecording} size="lg" variant="outline">
              <Square className="w-5 h-5 mr-2" />
              Stop Recording
            </Button>
          )}
        </div>

        {/* Preview and Upload */}
        {audioBlob && (
          <div className="space-y-3">
            <div className="flex justify-center space-x-2">
              <Button 
                onClick={playRecording} 
                disabled={isPlaying}
                variant="outline"
                size="sm"
              >
                <Play className="w-4 h-4 mr-1" />
                {isPlaying ? 'Playing...' : 'Preview'}
              </Button>
              
              <Button 
                onClick={uploadRecording} 
                disabled={isUploading}
                size="sm"
                className="bg-green-600 hover:bg-green-700"
              >
                <Check className="w-4 h-4 mr-1" />
                {isUploading ? 'Uploading...' : 'Save'}
              </Button>
            </div>
          </div>
        )}

        {/* File Upload Alternative */}
        <div className="text-center">
          <p className="text-sm text-gray-500 mb-2">Or upload an audio file</p>
          <p className="text-xs text-blue-600 mb-2">Supported: MP3, WAV, M4A, AAC, WebM, OGG, FLAC (max 10MB)</p>
          <input
            type="file"
            accept="audio/*,.mp3,.wav,.m4a,.aac,.webm,.ogg,.flac"
            onChange={handleFileUpload}
            className="hidden"
            disabled={isUploading}
            id={`file-upload-${wordId}`}
          />
          <label htmlFor={`file-upload-${wordId}`} className="cursor-pointer">
            <Button 
              variant="outline" 
              size="sm" 
              disabled={isUploading}
              asChild
            >
              <span>
                <Upload className="w-4 h-4 mr-2" />
                {isUploading ? 'Uploading...' : 'Upload File'}
              </span>
            </Button>
          </label>
        </div>

        {/* Troubleshooting */}
        <div className="text-center">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={checkMicrophoneAccess}
            className="text-xs"
          >
            🔧 Test Microphone Access
          </Button>
        </div>

        {/* Instructions */}
        <div className="text-xs text-gray-500 text-center">
          <p>Tips for best results:</p>
          <p>• Speak clearly and at normal pace</p>
          <p>• Use a quiet environment</p>
          <p>• Hold device close to your mouth</p>
          <p className="mt-2 text-blue-600">
            💡 If recording fails, try the file upload option or check browser permissions
          </p>
          <p className="mt-2 text-orange-600">
            ⚠️ For best compatibility, use MP3 or WAV format. M4A files may have playback issues on some devices.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default AudioRecorder;